import type { Networkish, SupTransactionType, SupWeb3Balance, SupWeb3TxSingleVault, SupWithdrawableBalance, SupWithdrawableToken } from "@/types";
import { FixedNumber } from "@ethersproject/bignumber";
import { sendGTMEvent } from "@next/third-parties/google";
import spindl from "@spindl-xyz/attribution";
import debounce from "lodash.debounce";

type AnalyticsEventName =
  | "wallet_connected"
  | "super_eth_detail_viewed"
  | "super_usdc_eth_detail_viewed"
  | "super_usdc_base_detail_viewed"
  | "bag_opened"
  | "deposit_completed"
  | "withdrawal_completed"
  | "explore_page_visited";

interface BaseEventProps {
  chainId?: number;
  timestamp?: number;
}

type EventProps = BaseEventProps & Record<string, unknown>;

/**
 * Tracks custom events in Spindl
 * @param eventName - Name of the event to track
 * @param eventProps - Additional properties for the event
 * @param identityProps - Optional identity properties
 */
export const trackAnalyticsEvent = (
  eventName: AnalyticsEventName,
  eventProps: EventProps = {},
  identityProps?: {
    address?: string;
  }
) => {
  if (typeof window === "undefined") return;
  console.log(`[Spindl Event]: Tracking ${eventName}`, eventName, eventProps, identityProps);

  const eventData = {
    ...eventProps,
    timestamp: eventProps.timestamp || Date.now(),
  };

  // sending DataLayer event (Google Tag Manager)
  sendGTMEvent({ event: eventName, ...eventData, ...identityProps });
  // sending custom event to Spindl
  spindl.track(eventName, eventData, identityProps).then(data => console.log("[Spindl Event]: Event sent successfully", data));
};

export const trackPageView = (path: string, address: string | undefined) => {
  const eventMap: Record<string, string> = {
    explore_page_visited: "/",
    super_eth_detail_viewed: "/vault/8x0dWMdugEMCvyTC1mfdx/",
    super_usdc_eth_detail_viewed: "/vault/vL7k-5ZgYCoFgi6kz2jIJ/",
    super_usdc_base_detail_viewed: "/vault/zLVQbgScIbXJuSz-NNsK-/",
  };

  const page = Object.keys(eventMap).find(key => path.includes(eventMap[key])) as AnalyticsEventName | undefined;
  if (page) {
    trackAnalyticsEvent(page, {}, { address });
  }
};

export const trackBagOpen = debounce((vaults: SupWeb3TxSingleVault[], type: SupTransactionType, address) => {
  trackAnalyticsEvent("bag_opened", { type, vaults: vaults.map(vault => vault.id) }, { address });
}, 1000);

export const trackTxFinished = debounce(
  (
    txHash: string,
    vaultList: SupWeb3TxSingleVault[],
    type: SupTransactionType,
    address: string | undefined,
    chainId: Networkish | undefined,
    selectedBalance: SupWeb3Balance | SupWithdrawableBalance | SupWithdrawableToken | undefined
  ) => {
    // Getting total amount depositing in USD
    const amount = vaultList.reduce((acc, singleVault) => acc.addUnsafe(singleVault.amountInFiat || FixedNumber.from(0)), FixedNumber.from(0));
    trackAnalyticsEvent(
      type === "deposit" ? "deposit_completed" : "withdrawal_completed",
      {
        amount,
        txHash: txHash,
        token: selectedBalance?.address,
        chainId: chainId ? Number(chainId) : undefined,
        vaults: vaultList.map(vault => vault.id),
      },
      { address }
    );
  },
  1000
);
